import type { Context, CreateContextInput } from '@pm/schemas';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Textarea,
} from '@pm/ui';
import { Folder, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { useContexts, useInvalidating } from '@/lib/queries';
import { useToast } from '@/lib/toast';

const TYPE_LABEL: Record<string, string> = {
  CLIENT: 'Cliente',
  PROJECT: 'Projeto',
  DEPARTMENT: 'Departamento',
  INTERNAL: 'Interno',
};

/**
 * Projetos, departamentos e áreas internas (SPEC §2). No modo Agência os clientes ficam em
 * "Clientes" e saem desta lista; no uso interno aparecem todos.
 */
export function ContextsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const agency = useMe().organization?.mode === 'AGENCY';
  const { data: all = [], isLoading, isError, error, refetch } = useContexts();
  const data = agency ? all.filter((c) => c.type !== 'CLIENT') : all;
  const [create, setCreate] = useState(false);
  const [f, setF] = useState<CreateContextInput>({ type: 'PROJECT', name: '', description: '' });
  const save = useInvalidating(
    (input: CreateContextInput) => api.post<Context>('/contexts', input),
    [['contexts']],
  );

  return (
    <div className="flex max-w-[960px] flex-col gap-gap-lg">
      <PageHeader
        title="Contextos"
        subtitle={
          agency
            ? 'Projetos, departamentos e áreas internas da agência. Clientes ficam em Clientes.'
            : 'Projetos, departamentos e áreas internas às quais as demandas se vinculam.'
        }
        actions={
          can('clients.create') ? (
            <Button variant="primary" onClick={() => setCreate(true)}>
              <Plus /> Novo contexto
            </Button>
          ) : null
        }
      />
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Folder}
            title="Nenhum contexto"
            description="Crie um projeto ou departamento para organizar as demandas."
          />
        ) : (
          <ul>
            {data.map((c) => (
              <li
                key={c.id}
                className="flex h-row cursor-pointer items-center gap-3 border-b border-border px-4 last:border-b-0 hover:bg-surface-2"
                onClick={() => navigate(`/demandas?context=${c.id}`)}
              >
                <Badge
                  tone={c.type === 'CLIENT' ? 'accent' : c.type === 'PROJECT' ? 'info' : 'neutral'}
                >
                  {TYPE_LABEL[c.type]}
                </Badge>
                <span className="font-medium">{c.name}</span>
                {c.description ? (
                  <span className="truncate text-sm text-text-3">{c.description}</span>
                ) : null}
                {c.status === 'ARCHIVED' ? (
                  <Badge tone="neutral" className="ml-auto">
                    Arquivado
                  </Badge>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent title="Novo contexto" size="sm">
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(
                { ...f, description: f.description || undefined },
                {
                  onSuccess: () => (
                    toast.success('Contexto criado'),
                    setCreate(false),
                    setF({ type: 'PROJECT', name: '', description: '' })
                  ),
                  onError: (err) => toast.error(err),
                },
              );
            }}
          >
            <Field label="Tipo" htmlFor="cx-type">
              <Select
                id="cx-type"
                value={f.type}
                onChange={(e) => setF({ ...f, type: e.target.value as CreateContextInput['type'] })}
              >
                <option value="PROJECT">Projeto</option>
                <option value="DEPARTMENT">Departamento</option>
                <option value="INTERNAL">Interno</option>
              </Select>
            </Field>
            <Field label="Nome" htmlFor="cx-name">
              <Input
                id="cx-name"
                autoFocus
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            </Field>
            <Field label="Descrição" htmlFor="cx-desc">
              <Textarea
                id="cx-desc"
                value={f.description ?? ''}
                onChange={(e) => setF({ ...f, description: e.target.value })}
                className="min-h-[72px]"
              />
            </Field>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={() => setCreate(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                loading={save.isPending}
                disabled={f.name.trim().length < 2}
              >
                Criar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
